import React, { useEffect, useLayoutEffect, useState } from "react";
import getApiBaseUrl from "../utils/api";

// ============================================================
// SHARED HELPERS FOR THE TOEFL iBT (2026 FORMAT) SECTIONS
// ============================================================
//
// From 21 January 2026 every TOEFL iBT section is reported on a 1–6 band
// scale in half-band steps, and the total is the average of the four
// section bands rounded to the nearest 0.5. ETS also publishes a
// comparable 0–30 section score for the transition period — we save
// that as `toeflScore` so the profile's /120 total keeps working.

export const C = {
  green: "#05914f",
  greenDark: "#0c572e",
  greenText: "#1a5c3a",
  accent: "#2e7d52",
  border: "#c8e6d4",
  soft: "#f4faf7",
  softer: "#f9fefb",
  muted: "#6b7a74",
  red: "#d93646",
  amber: "#d9822b",
};

export const roundHalf = (n) => Math.round(n * 2) / 2;

const clampBand = (b) => Math.min(6, Math.max(1, roundHalf(b)));

// Comparable 0–30 section score for each band (practice-test estimate)
const BAND_TO_SCALED = {
  6: 30, 5.5: 28, 5: 25, 4.5: 22, 4: 19, 3.5: 15,
  3: 10, 2.5: 6, 2: 4, 1.5: 2, 1: 1,
};

export const bandToScaled = (band) => BAND_TO_SCALED[clampBand(band)] ?? 0;

export const cefrForBand = (band) =>
  band >= 6 ? "C2" : band >= 5 ? "C1" : band >= 4 ? "B2" : band >= 3 ? "B1" : band >= 2 ? "A2" : "A1";

export const BAND_DESCRIPTIONS = {
  C2: "Mastery: you understand and use English precisely in demanding academic settings.",
  C1: "Advanced: you handle complex academic material and express ideas fluently.",
  B2: "Upper-intermediate: you follow most academic content and communicate effectively.",
  B1: "Intermediate: you manage familiar topics but struggle with denser academic language.",
  A2: "Elementary: you understand simple, everyday language.",
  A1: "Beginner: build core vocabulary and grammar before attempting a full test.",
};

// ── Multistage adaptive scoring (Reading & Listening) ─────────────
// Module 1 is the same for everyone. Scoring at least ROUTER_THRESHOLD
// on it routes the test taker to the harder Module 2 ("upper"); otherwise
// they get the easier one ("lower"), which caps the achievable band.
export const ROUTER_THRESHOLD = 0.6;

export const routeForModule1 = (correct, total) =>
  total > 0 && correct / total >= ROUTER_THRESHOLD ? "upper" : "lower";

const UPPER_STEPS = [[0.95, 6], [0.88, 5.5], [0.8, 5], [0.7, 4.5], [0.6, 4], [0.5, 3.5]];
const LOWER_STEPS = [[0.9, 4.5], [0.8, 4], [0.7, 3.5], [0.6, 3], [0.5, 2.5], [0.4, 2], [0.25, 1.5]];

export function adaptiveBand(fraction, path) {
  const steps = path === "upper" ? UPPER_STEPS : LOWER_STEPS;
  const floor = path === "upper" ? 3 : 1;
  for (const [min, band] of steps) if (fraction >= min) return band;
  return floor;
}

// Non-adaptive sections (Writing & Speaking): fraction 0–1 of the
// maximum rubric score → band.
const LINEAR_STEPS = [
  [0.95, 6], [0.88, 5.5], [0.8, 5], [0.72, 4.5], [0.64, 4], [0.55, 3.5],
  [0.45, 3], [0.35, 2.5], [0.25, 2], [0.15, 1.5],
];

export function linearBand(fraction) {
  for (const [min, band] of LINEAR_STEPS) if (fraction >= min) return band;
  return 1;
}

// Builds the common score fields every section saves.
export function bandSummary(band, path = null) {
  const cefr = cefrForBand(band);
  return {
    bandScore: band,
    cefr,
    toeflScore: bandToScaled(band),
    adaptivePath: path,
    bandDescription: BAND_DESCRIPTIONS[cefr],
  };
}

// ── Saving ───────────────────────────────────────────────────────
// Returns "saved" | "unauthorized" | "error" | "network" — the result
// page shows a warning for anything other than "saved".
export async function saveToeflResult(testName, result) {
  try {
    const response = await fetch(`${getApiBaseUrl()}/tests/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ testName, result }),
    });
    if (!response.ok) {
      console.error(`Failed to save ${testName}:`, response.status, await response.text());
      return response.status === 401 ? "unauthorized" : "error";
    }
    return "saved";
  } catch (err) {
    console.error(`Failed to save ${testName}:`, err);
    return "network";
  }
}

// ── Page helpers ─────────────────────────────────────────────────
export const scrollToTop = () => {
  window.scrollTo(0, 0);
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

// Opens the page from the very top, so the heading is never hidden
// behind the navbar by the browser's scroll restoration.
export function usePageTop() {
  useLayoutEffect(() => {
    const hasRestoration = "scrollRestoration" in window.history;
    const prev = hasRestoration ? window.history.scrollRestoration : null;
    if (hasRestoration) window.history.scrollRestoration = "manual";

    scrollToTop();
    const t1 = setTimeout(scrollToTop, 0);
    const t2 = setTimeout(scrollToTop, 150);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (hasRestoration) window.history.scrollRestoration = prev;
    };
  }, []);
}

export function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export function formatTime(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, "0")}`;
}

// Counts down once per second while `running`; calls onExpire once at 0.
export function useCountdown(initialSeconds, running, onExpire) {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const expireRef = React.useRef(onExpire);
  expireRef.current = onExpire;

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setTimeLeft((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (running && timeLeft === 0 && expireRef.current) expireRef.current();
  }, [timeLeft, running]);

  return [timeLeft, setTimeLeft];
}

export function TimerBadge({ timeLeft, isMobile, label = "Time Left" }) {
  const low = timeLeft <= 5 * 60;
  const critical = timeLeft <= 60;
  return (
    <div
      style={{
        background: critical ? "#fdeceb" : low ? "#fff6e5" : "#eaf7f0",
        border: `1.5px solid ${critical ? "#e05a4e" : low ? "#e0a63c" : C.accent}`,
        borderRadius: "30px",
        padding: isMobile ? "5px 12px" : "6px 16px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#555" }}>{label}</span>
      <span
        style={{
          fontSize: isMobile ? "0.95rem" : "1.05rem",
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
          color: critical ? "#c0392b" : low ? "#a06a10" : C.greenText,
        }}
      >
        {formatTime(timeLeft)}
      </span>
    </div>
  );
}

// Full-page spinner shown while a section is being scored and saved.
export function SubmittingScreen({ isMobile, message = "Submitting your answers..." }) {
  return (
    <div style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "96px 14px 24px" : "160px 24px 40px" }}>
      <style>{"@keyframes tx-spin { to { transform: rotate(360deg); } }"}</style>
      <div
        style={{
          maxWidth: "460px",
          margin: "0 auto",
          textAlign: "center",
          padding: isMobile ? "32px 20px" : "44px 30px",
          background: C.softer,
          border: `1px solid ${C.border}`,
          borderRadius: "16px",
        }}
      >
        <div
          style={{
            width: "44px",
            height: "44px",
            margin: "0 auto",
            borderRadius: "50%",
            border: "4px solid #d9ebe0",
            borderTopColor: C.green,
            animation: "tx-spin 0.9s linear infinite",
          }}
        />
        <h2 style={{ margin: "20px 0 6px", color: "#0c3d24", fontSize: isMobile ? "1.15rem" : "1.35rem" }}>
          {message}
        </h2>
        <p style={{ margin: 0, color: C.muted, fontSize: "0.92rem" }}>
          Please wait while we calculate your score.
        </p>
      </div>
    </div>
  );
}

// ── Text-to-speech ───────────────────────────────────────────────
// Recordings are synthesized in the browser. Lines are spoken one after
// another, with a different voice (or pitch) per speaker so
// conversations are easy to follow.
const FEMALE_HINTS = /female|woman|zira|samantha|victoria|karen|moira|tessa|susan|hazel|jenny|aria|libby|sonia|emma|ava|allison|google us english/i;
const MALE_HINTS = /male|man|david|daniel|alex|fred|george|mark|guy|ryan|james|tom|google uk english male/i;

function pickVoices() {
  const synth = window.speechSynthesis;
  const english = synth.getVoices().filter((v) => /^en[-_]/i.test(v.lang));
  const female = english.find((v) => FEMALE_HINTS.test(v.name) && !/\bmale\b/i.test(v.name));
  const male = english.find((v) => MALE_HINTS.test(v.name) && v !== female);
  return { female: female || english[0] || null, male: male || english[1] || english[0] || null };
}

export const speechSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

// lines: [{ speaker: "Man" | "Woman" | ..., gender: "m" | "f", text }]
// Returns a cancel function.
export function speakLines(lines, { rate = 0.95, onEnd } = {}) {
  if (!speechSupported()) {
    if (onEnd) onEnd();
    return () => {};
  }
  const synth = window.speechSynthesis;
  synth.cancel();
  const voices = pickVoices();
  let cancelled = false;

  // Chrome silently stops utterances longer than ~15 seconds, so long
  // lines (lectures, announcements) are queued one sentence at a time.
  const chunks = lines.flatMap((line) =>
    (line.text.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g) || [line.text])
      .map((t) => t.trim())
      .filter(Boolean)
      .map((text) => ({ ...line, text }))
  );

  chunks.forEach((line, i) => {
    const utter = new SpeechSynthesisUtterance(line.text);
    const isMale = line.gender === "m";
    const voice = isMale ? voices.male : voices.female;
    if (voice) utter.voice = voice;
    // Same voice for both speakers? Separate them by pitch instead.
    utter.pitch = voices.male === voices.female ? (isMale ? 0.8 : 1.2) : 1;
    utter.rate = rate;
    if (i === chunks.length - 1) {
      utter.onend = () => { if (!cancelled && onEnd) onEnd(); };
      utter.onerror = () => { if (!cancelled && onEnd) onEnd(); };
    }
    synth.speak(utter);
  });

  return () => {
    cancelled = true;
    synth.cancel();
  };
}

// Generous upper bound on how long `lines` take to speak — used as a
// fallback in case the browser never fires the final onend event.
export const estimateSpeechSeconds = (lines) => {
  const words = lines.reduce((n, l) => n + l.text.split(/\s+/).length, 0);
  return Math.ceil(words / 2.2) + 6;
};

// Voices load asynchronously in Chrome; call once on mount.
export function useVoicesReady() {
  useEffect(() => {
    if (!speechSupported()) return undefined;
    const synth = window.speechSynthesis;
    synth.getVoices();
    const handler = () => synth.getVoices();
    synth.addEventListener?.("voiceschanged", handler);
    return () => {
      synth.removeEventListener?.("voiceschanged", handler);
      synth.cancel();
    };
  }, []);
}
